import {
  BooleanInput,
  Create,
  FormDataConsumer,
  FormTab,
  NumberInput,
  SelectInput,
  TabbedForm,
  TextInput,
  maxValue,
  minLength,
  minValue,
  required
} from 'react-admin';
import { ImageUploadInput, PriceInput, Tooltip } from '../../components';
import CoordinatesMapInput from '../../components/CoordinatesMapInput';
import { CEPInput, CNPJInput, PhoneInput, RtiTokenInput, StateInput } from '../../components/Inputs';
import Permission from '../../components/Permission';
import { PERMISSIONS, minimumValueCashbackToolTip } from '../../constants';
import { Box, Grid } from '@mui/material';
import { IntegrationType } from '../../models';
import { SislavFields, clearSislavFields } from '../../components/SislavFields';
import { normalizeCredentials, requiredWithCredentials, validateCredentialHost } from '../../lib/stationPartnerForm';
import { validateLatitude, validateLongitude } from '../../lib/formValidators';

const validateCredentials = credentials =>
  credentials &&
  credentials.host &&
  credentials.port &&
  credentials.database &&
  credentials.user &&
  credentials.password

const validateName = [required(), minLength(2)]
const validateRequired = [required()]
// "-19,92" no campo numérico virava -1992: 67 parceiros com coordenada impossível em 04/11/2026 (pedido ao dev #10).
const validateLatitudeRequired = [required(), validateLatitude]
const validateLongitudeRequired = [required(), validateLongitude]
const validateCredentialsTogether = [requiredWithCredentials]
const validateCredentialsHost = [requiredWithCredentials, validateCredentialHost]

// Aba Credenciais tocada sem preencher nada manda {"port":null}: tratada como "não informada".
const transformPartner = (data) => clearSislavFields(normalizeCredentials(data))

const CreatePartner = (props) =>
  <Permission permission={PERMISSIONS.CREATE_PARTNER}>
    <Create {...props} title="Cadastrar novo parceiro" transform={transformPartner}>
      <TabbedForm sanitizeEmptyValues>
        <FormTab label="Geral">
          <Box sx={{ maxWidth: 800, width: '100%' }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextInput source="name" label="Nome" validate={validateName} InputProps={{ autoComplete: 'off' }} fullWidth />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <CNPJInput source="cnpj" label="CNPJ" validate={validateRequired} fullWidth />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <PhoneInput InputProps={{ autoComplete: 'off' }} source="phone" label="Telefone" isRequired fullWidth />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextInput source="contact" label="Contato" InputProps={{ autoComplete: 'off' }} fullWidth />
              </Grid>
            </Grid>
            <ImageUploadInput />
            <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <BooleanInput label="Aceita cashback?" source="acceptsCashback" defaultValue={false} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Tooltip sxContainer={{ alignItems: 'flex-start' }} title={minimumValueCashbackToolTip} placement="top-start">
                  <PriceInput source="minimumValueToRedeemCashback" label="Mínimo para resgate de cashback" fullWidth />
                </Tooltip>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <NumberInput source="negotiatedDiscount" label="Desconto negociado (Cashback)" min={0.01} max={100} step={0.01} validate={[minValue(0.01), maxValue(100)]} placeholder="%" fullWidth />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <NumberInput source="productNegotiatedDiscount" label="Desconto negociado (Produtos)" min={0.01} max={100} step={0.01} validate={[minValue(0.01), maxValue(100)]} placeholder="%" fullWidth />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <BooleanInput label="Possui comanda eletrônica?" source="hasOrderSheet" defaultValue={false} />
              </Grid>
            </Grid>
            <Permission permission={PERMISSIONS.ADMIN}>
              <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <SelectInput label="Integração" emptyText="ra.action.clear_input" source="integration" choices={IntegrationType.integrationChoices} fullWidth />
                </Grid>
                <FormDataConsumer>
                  {({ formData }) => (
                    formData.integration === IntegrationType.rti &&
                    <Grid size={{ xs: 12, md: 4 }}>
                      <RtiTokenInput label="Token RTI" source="rtiToken" fullWidth />
                    </Grid>
                  )}
                </FormDataConsumer>
                <SislavFields />
              </Grid>
            </Permission>
          </Box>
        </FormTab>
        <FormTab label="Endereço">
          <Grid container spacing={2} sx={{ maxWidth: 800 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextInput source="street" label="Rua" validate={validateRequired} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextInput
                source="streetNumber"
                label="Número"
                validate={validateRequired}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ maxWidth: 800 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <CEPInput source="cep" label="CEP" validate={validateRequired} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextInput
                source="neighbourhood"
                label="Bairro"
                validate={validateRequired}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextInput source="city" label="Cidade" validate={validateRequired} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <StateInput source="state" label="Estado" validate={validateRequired} fullWidth />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ maxWidth: 800 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <NumberInput source="latitude" label="Latitude" validate={validateLatitudeRequired} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <NumberInput source="longitude" label="Longitude" validate={validateLongitudeRequired} fullWidth />
            </Grid>
          </Grid>
        </FormTab>
        <FormTab label="GPS">
          <CoordinatesMapInput />
        </FormTab>
        <Permission permission={PERMISSIONS.ADMIN}>
          <FormTab label="Credenciais">
            <TextInput source="credentials.host" label="Host ou ip" validate={validateCredentialsHost} InputProps={{ autoComplete: 'off' }} sx={{ minWidth: 300 }} />
            <NumberInput source="credentials.port" label="Porta" validate={validateCredentialsTogether} InputProps={{ autoComplete: 'off' }} sx={{ minWidth: 300 }} />
            <TextInput source="credentials.user" label="Usuário" validate={validateCredentialsTogether} InputProps={{ autoComplete: 'off' }} sx={{ minWidth: 300 }} />
            <TextInput source="credentials.password" label="Senha" validate={validateCredentialsTogether} type="password" InputProps={{ autoComplete: 'off' }} sx={{ minWidth: 300 }} />
            <TextInput source="credentials.database" label="Banco" validate={validateCredentialsTogether} InputProps={{ autoComplete: 'off' }} sx={{ minWidth: 300 }} />
          </FormTab>
        </Permission>
      </TabbedForm>
    </Create>
  </Permission>

export default CreatePartner;